import { useLocation } from "wouter";
import { useQueryClient } from "@tanstack/react-query";
import { useGetStudy } from "@/api/studio/studies/queries";
import { getStudy } from "@/api/studio/studies/api";
import { getNextRoute, StudyStep, StudyType } from "../utils/study-flow.config";
import { useGlobalStore } from "@/stores/global-store";

/**
 * useStudyNavigation hook
 *
 * Provides type-aware navigation for study flows.
 * Automatically determines the correct next step based on the study type.
 * Considers user features (like memoryEmotion) for conditional routing.
 *
 * @example
 * const { navigateToNext } = useStudyNavigation(projectId, studyId, "study-select");
 * navigateToNext();
 */
export const useStudyNavigation = (projectId: string, studyId: string, currentStep: StudyStep) => {
  const [, navigate] = useLocation();
  const { data: study } = useGetStudy(projectId, studyId);
  const queryClient = useQueryClient();
  const entitlements = useGlobalStore((state) => state.entitlements);

  const hasMemoryEmotion = entitlements.some((entitlement) => entitlement.name === "memoryEmotion");

  /**
   * Resolves the study's type, waiting on the studies query if it hasn't loaded into the cache
   * yet rather than guessing — guessing wrong here sends the user down a different study type's
   * flow entirely (e.g. a video study routed through the asset reports).
   */
  const resolveStudyType = async (): Promise<StudyType> => {
    if (study?.type) return study.type;
    const resp = await queryClient.ensureQueryData({
      queryKey: ["studies", studyId],
      queryFn: () => getStudy(projectId, studyId),
    });
    return resp.study.type ?? "asset";
  };

  /**
   * Navigate to the next step in the flow based on your current step and study type
   */
  const navigateToNext = async () => {
    const studyType = await resolveStudyType();
    const nextRoute = getNextRoute(studyType, currentStep, projectId, studyId, { hasMemoryEmotion });
    if (nextRoute) {
      navigate(nextRoute);
    }
  };

  /**
   * Navigate to a specific route
   */
  const navigateTo = (route: string) => {
    navigate(route);
  };

  /**
   * Check if navigation to next step is possible
   */
  const canNavigateNext = async () => {
    const studyType = await resolveStudyType();
    const nextRoute = getNextRoute(studyType, currentStep, projectId, studyId, { hasMemoryEmotion });
    return nextRoute !== null;
  };

  /* Return */
  return {
    navigateToNext,
    navigateTo,
    canNavigateNext,
    studyType: study?.type,
    hasMemoryEmotion,
  };
};
